import React, { Suspense, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SceneA = React.lazy(() => import("@/components/scenes/SceneA"));
const SceneE = React.lazy(() => import("@/components/scenes/SceneE"));

/**
 * SceneLab — hidden verification surface for Phase 4.
 *
 * Route:      /_scene-lab
 * Discovery:  Not linked from anywhere. Not in sitemap.xml. Helmet emits
 *             `<meta name="robots" content="noindex, nofollow" />`.
 * Debug:      Append ?debug=1 to render an FPS/Draws readout (drei's <Stats/>)
 *             overlaid on each Canvas. Off by default so the tester can grab
 *             clean screenshots.
 *
 * Both scenes are loaded via React.lazy + Suspense at this consumer level
 * per the Phase 4 brief; the Suspense fallback is a static placeholder that
 * matches the scene's aspect ratio.
 */
export default function SceneLab() {
  const { search } = useLocation();
  const debug = new URLSearchParams(search).get("debug") === "1";

  const [progressA, setProgressA] = useState(0);
  const [progressE, setProgressE] = useState(0);
  const [staticA, setStaticA] = useState(false);
  const [staticE, setStaticE] = useState(false);

  return (
    <div
      className="min-h-screen bg-surface-base text-ink-primary"
      data-testid="scene-lab"
    >
      <Helmet>
        <title>Scene lab — Frac (internal)</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="container-frac pt-12 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow">INTERNAL · SCENE LAB</span>
            <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Phase 4 — Scenes A &amp; E, in isolation
            </h1>
            <p className="mt-3 max-w-2xl text-base text-ink-secondary">
              Both scenes are lazy-loaded and driven by a manual scroll slider
              rather than the page scroll, so acceptance can be verified without
              the homepage or the data page existing yet. Append{" "}
              <code className="rounded bg-surface-elevated px-1.5 py-0.5 text-[13px] text-ink-primary">
                ?debug=1
              </code>{" "}
              to overlay FPS &amp; draw-call stats on each canvas.
            </p>
          </div>
          <div className="rounded-md border border-hairline bg-surface-elevated/60 px-3 py-2 text-[12px] font-semibold uppercase tracking-caps text-eyebrow">
            /_scene-lab
          </div>
        </div>
      </header>

      <SceneSection
        eyebrow="A"
        title="The Behavioural Field"
        summary="Volumetric point field. Scroll 0 → 1 coalesces the cloud into a lattice grid. Auto Y-rotation; heavy-damped mouse parallax; noise amplitude decays as the grid locks in."
        progress={progressA}
        onProgress={setProgressA}
        forceStatic={staticA}
        onForceStatic={setStaticA}
        debug={debug}
        testIdBase="scene-a"
      >
        <SceneSurface aspectRatio="16 / 9">
          <Suspense fallback={<StaticFallback />}>
            <SceneA
              scrollProgress={progressA}
              forceStatic={staticA}
              debug={debug}
              style={{ inset: 0, position: "absolute" }}
            />
          </Suspense>
        </SceneSurface>
      </SceneSection>

      <SceneSection
        eyebrow="E"
        title="The Minting Sequence"
        summary="Three acts scrubbed by scroll: (1) structured signal cluster (2) crystallisation into a single icosahedron (3) replication into an 8-unit ledger with one MINTED · UNIQUE. Hover a locked unit to reassign the highlight; leave for 2s and highlight snaps back to unit 4."
        progress={progressE}
        onProgress={setProgressE}
        forceStatic={staticE}
        onForceStatic={setStaticE}
        debug={debug}
        testIdBase="scene-e"
        showActMarkers
      >
        <SceneSurface aspectRatio="16 / 9">
          <Suspense fallback={<StaticFallback />}>
            <SceneE
              scrollProgress={progressE}
              forceStatic={staticE}
              debug={debug}
              style={{ inset: 0, position: "absolute" }}
            />
          </Suspense>
        </SceneSurface>
      </SceneSection>

      <footer className="container-frac pb-16">
        <p className="text-xs text-ink-tertiary">
          Not in sitemap · noindex · not linked from marketing routes.
        </p>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout helpers                                                             */
/* -------------------------------------------------------------------------- */

function SceneSection({
  eyebrow,
  title,
  summary,
  progress,
  onProgress,
  forceStatic,
  onForceStatic,
  debug,
  testIdBase,
  showActMarkers = false,
  children,
}) {
  return (
    <section
      className="container-frac border-t border-hairline py-12"
      data-testid={`${testIdBase}-section`}
    >
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow">SCENE · {eyebrow}</span>
          <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
            {title}
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-ink-secondary">{summary}</p>
        </div>
        <div className="flex items-center gap-3 text-[12px] uppercase tracking-caps text-ink-tertiary">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={forceStatic}
              onChange={(e) => onForceStatic(e.target.checked)}
              data-testid={`${testIdBase}-force-static`}
              className="h-4 w-4 accent-[var(--frac-violet)]"
            />
            Force static frame
          </label>
          {debug && (
            <span className="rounded-full border border-frac-violet/40 px-2 py-0.5 text-frac-violet">
              debug · on
            </span>
          )}
        </div>
      </div>

      {children}

      <div className="mt-5 grid gap-2">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-caps text-ink-tertiary">
          <span>Scroll progress</span>
          <span
            className="font-mono text-ink-primary"
            data-testid={`${testIdBase}-progress-readout`}
          >
            {progress.toFixed(3)}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={progress}
          onChange={(e) => onProgress(parseFloat(e.target.value))}
          data-testid={`${testIdBase}-progress-slider`}
          aria-label={`${testIdBase} scroll progress`}
          className="h-2 w-full appearance-none rounded-full bg-surface-elevated accent-[var(--frac-violet)]"
        />
        {showActMarkers && (
          <div
            className="relative mt-1 h-4 text-[10px] uppercase tracking-caps text-ink-tertiary"
            aria-hidden="true"
          >
            <span className="absolute left-[0%]">Act 1</span>
            <span className="absolute left-[33%] -translate-x-1/2">Act 2</span>
            <span className="absolute left-[66%] -translate-x-1/2">Act 3</span>
            <span className="absolute right-[0%]">End</span>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {presetSteps().map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => onProgress(s.value)}
            data-testid={`${testIdBase}-preset-${s.label
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")}`}
            className="rounded-full border border-hairline bg-surface-elevated px-3 py-1 text-[11px] uppercase tracking-caps text-ink-secondary transition-colors duration-fast hover:border-frac-violet hover:text-ink-primary"
          >
            {s.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function presetSteps() {
  return [
    { label: "0.00", value: 0 },
    { label: "0.25", value: 0.25 },
    { label: "0.33", value: 0.33 },
    { label: "0.50", value: 0.5 },
    { label: "0.66", value: 0.66 },
    { label: "0.85", value: 0.85 },
    { label: "1.00", value: 1 },
  ];
}

function SceneSurface({ aspectRatio, children }) {
  const styleObj = useMemo(
    () => ({
      position: "relative",
      width: "100%",
      aspectRatio,
      overflow: "hidden",
      borderRadius: "var(--radius-lg)",
      border: "1px solid var(--border-hairline)",
      background: "var(--surface-base)",
    }),
    [aspectRatio],
  );
  return <div style={styleObj}>{children}</div>;
}

function StaticFallback() {
  // Aspect-ratio matched placeholder — parent SceneSurface provides the box.
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--surface-base)",
      }}
    />
  );
}
